import React, { useEffect, useState } from "react";
import axios from "axios";

/**
 * PitruPakshaDonationForm.jsx
 * - Preserves exact seva configuration, Razorpay payment & Google Sheets logging from Pitru Paksha Seva
 * - Sevas: Sadhu Bhojana Seva, Gau Seva, Annadan Seva, Archana Seva, Sponsor Bhagavad Gita
 * - Built with fully responsive Tailwind CSS
 */

const AMOUNTS = [10000, 5000, 2500, 1000];
const BHAGVATGITAAMOUNTS = [
  { amount: 3000, text: "10 Bhagavad Gita" },
  { amount: 6000, text: "20 Bhagavad Gita" },
  { amount: 30000, text: "100 Bhagavad Gita" },
];

const PitruPakshaDonationForm = () => {
  const [formData, setFormData] = useState({
    // Seva toggles
    sadhuBhojanaSeva: false,
    gauSeva: false,
    annadanSeva: false,
    archanaSeva: false,
    sponsorGita: false,

    // Selected / Custom amounts per seva
    selectedSadhuAmount: null,
    customSadhuAmount: "",

    selectedGauAmount: null,
    customGauAmount: "",

    selectedAnnadanAmount: null,
    customAnnadanAmount: "",

    selectedArchanaAmount: null,
    customArchanaAmount: "",

    selectedGitaAmount: null,
    customGitaAmount: "",

    // In memory of
    honoureeName: "",
    relationship: "",

    // Personal details
    donorName: "",
    email: "",
    mobile: "",
    dob: "",

    // Certificates / logistics
    receive80G: false,
    panNumber: "",
    receiveMahaPrasadam: false,

    // Address for prasadam / 80G
    mahaPrasadamDetails: {
      doorNumber: "",
      houseName: "",
      streetName: "",
      area: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [show80GDetails, setShow80GDetails] = useState(false);
  const [showMahaPrasadamDetails, setShowMahaPrasadamDetails] = useState(false);

  useEffect(() => {
    const loadRazorpay = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadRazorpay();
  }, []);

  // ---------------- Helpers ----------------
  const formatINR = (n) => (n || n === 0 ? n.toLocaleString("en-IN") : "");

  // prefer selected (button) amount; else fallback to parsed custom input
  const getSevaAmount = (selected, custom) => {
    if (selected) return selected;
    const parsed = parseFloat(custom || "0");
    return isNaN(parsed) ? 0 : parsed;
  };

  const getTotalAmount = () => {
    const sadhu = getSevaAmount(
      formData.selectedSadhuAmount,
      formData.customSadhuAmount,
    );
    const gau = getSevaAmount(
      formData.selectedGauAmount,
      formData.customGauAmount,
    );
    const annadan = getSevaAmount(
      formData.selectedAnnadanAmount,
      formData.customAnnadanAmount,
    );
    const archana = getSevaAmount(
      formData.selectedArchanaAmount,
      formData.customArchanaAmount,
    );
    const gita = getSevaAmount(
      formData.selectedGitaAmount,
      formData.customGitaAmount,
    );
    return sadhu + gau + annadan + archana + gita;
  };

  const selectedSevasSummary = () => {
    const parts = [];
    if (formData.sadhuBhojanaSeva) {
      const amt = getSevaAmount(
        formData.selectedSadhuAmount,
        formData.customSadhuAmount,
      );
      if (amt > 0) parts.push(`Sadhu Bhojana Seva: ₹${formatINR(amt)}`);
    }
    if (formData.gauSeva) {
      const amt = getSevaAmount(
        formData.selectedGauAmount,
        formData.customGauAmount,
      );
      if (amt > 0) parts.push(`Gau Seva: ₹${formatINR(amt)}`);
    }
    if (formData.annadanSeva) {
      const amt = getSevaAmount(
        formData.selectedAnnadanAmount,
        formData.customAnnadanAmount,
      );
      if (amt > 0) parts.push(`Annadan Seva: ₹${formatINR(amt)}`);
    }
    if (formData.archanaSeva) {
      const amt = getSevaAmount(
        formData.selectedArchanaAmount,
        formData.customArchanaAmount,
      );
      if (amt > 0) parts.push(`Archana Seva: ₹${formatINR(amt)}`);
    }
    if (formData.sponsorGita) {
      const amt = getSevaAmount(
        formData.selectedGitaAmount,
        formData.customGitaAmount,
      );
      if (amt > 0) parts.push(`Sponsor Bhagavad Gita: ₹${formatINR(amt)}`);
    }
    return parts.join(" | ");
  };

  // ---------------- Handlers ----------------
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      if (name in prev.mahaPrasadamDetails) {
        return {
          ...prev,
          mahaPrasadamDetails: {
            ...prev.mahaPrasadamDetails,
            [name]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const handleAmountSelect = (section, amount) => {
    setFormData((prev) => {
      const updated = { ...prev };
      if (section === "sadhu") {
        updated.selectedSadhuAmount =
          prev.selectedSadhuAmount === amount ? null : amount;
        updated.customSadhuAmount = "";
      } else if (section === "gau") {
        updated.selectedGauAmount =
          prev.selectedGauAmount === amount ? null : amount;
        updated.customGauAmount = "";
      } else if (section === "annadan") {
        updated.selectedAnnadanAmount =
          prev.selectedAnnadanAmount === amount ? null : amount;
        updated.customAnnadanAmount = "";
      } else if (section === "archana") {
        updated.selectedArchanaAmount =
          prev.selectedArchanaAmount === amount ? null : amount;
        updated.customArchanaAmount = "";
      } else if (section === "gita") {
        updated.selectedGitaAmount =
          prev.selectedGitaAmount === amount ? null : amount;
        updated.customGitaAmount = "";
      }
      return updated;
    });
  };

  const handleCustomChange = (keySelected, keyCustom, value) => {
    setFormData((prev) => ({
      ...prev,
      [keyCustom]: value,
      [keySelected]: null,
    }));
  };

  const handle80GCheckboxChange = (e) => {
    setShow80GDetails(e.target.checked);
    handleInputChange(e);
  };

  const handleMahaPrasadamCheckboxChange = (e) => {
    setShowMahaPrasadamDetails(e.target.checked);
    handleInputChange(e);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.donorName) newErrors.donorName = "Donor name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";

    if (formData.receive80G && !formData.panNumber)
      newErrors.panNumber = "PAN number is required for 80G certificate";

    if (getTotalAmount() <= 0)
      newErrors.total =
        "Please select at least one seva amount or enter a custom amount.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------------- Submit + Payment ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const totalAmount = getTotalAmount();
    let isFormDataStored = false;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payment/create-order`,
        {
          amount: totalAmount,
          currency: "INR",
        },
      );

      const order = response.data;
      const sevaDescription = selectedSevasSummary() || "Donation Payment";

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: totalAmount * 100,
        currency: "INR",
        name: "ISKCON Donation Pitru Paksha 2026",
        description: sevaDescription,
        order_id: order.id,
        notes: {
          donorName: formData.donorName,
          donorPhoneNumber: formData.mobile,
          donorEmail: formData.email,
          honoureeName: formData.honoureeName,
          relationship: formData.relationship,
        },
        handler: async function (response) {
          try {
            const verificationResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/payment/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                donationDetails: { ...formData, amount: totalAmount },
              },
            );

            if (verificationResponse.data.success) {
              alert("Payment Successful!");
              if (!isFormDataStored) {
                await storeFormData("Payment Successful");
                isFormDataStored = true;
              }
            }
          } catch (err) {
            console.error(err);
            alert("Payment verification failed");
            if (!isFormDataStored) {
              await storeFormData("Payment Failed");
              isFormDataStored = true;
            }
          }
        },
        prefill: {
          name: formData.donorName,
          email: formData.email,
          contact: formData.mobile,
        },
        theme: { color: "#C61D21" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", async function (response) {
        alert("Payment Failed: " + response.error.description);
        if (!isFormDataStored) {
          await storeFormData("Payment Failed");
          isFormDataStored = true;
        }
      });
    } catch (error) {
      console.error("Error initiating payment:", error);
      alert("Failed to initiate payment. Please try again.");
      if (!isFormDataStored) {
        await storeFormData("Payment Failed");
        isFormDataStored = true;
      }
    }
  };

  // ---------------- Store to Google Sheets ----------------
  const storeFormData = async (paymentStatus) => {
    const totalDonationAmount = getTotalAmount();

    const formDataToSend = new URLSearchParams();
    formDataToSend.append("paymentStatus", paymentStatus);

    formDataToSend.append(
      "sadhuBhojanaSeva",
      formData.sadhuBhojanaSeva ? "Yes" : "No",
    );
    formDataToSend.append(
      "sadhuBhojanaAmount",
      getSevaAmount(formData.selectedSadhuAmount, formData.customSadhuAmount),
    );

    formDataToSend.append("gauSeva", formData.gauSeva ? "Yes" : "No");
    formDataToSend.append(
      "gauSevaAmount",
      getSevaAmount(formData.selectedGauAmount, formData.customGauAmount),
    );

    formDataToSend.append("annadanSeva", formData.annadanSeva ? "Yes" : "No");
    formDataToSend.append(
      "annadanSevaAmount",
      getSevaAmount(
        formData.selectedAnnadanAmount,
        formData.customAnnadanAmount,
      ),
    );

    formDataToSend.append("archanaSeva", formData.archanaSeva ? "Yes" : "No");
    formDataToSend.append(
      "archanaSevaAmount",
      getSevaAmount(
        formData.selectedArchanaAmount,
        formData.customArchanaAmount,
      ),
    );

    formDataToSend.append("sponsorGita", formData.sponsorGita ? "Yes" : "No");
    formDataToSend.append(
      "sponsorGitaAmount",
      getSevaAmount(formData.selectedGitaAmount, formData.customGitaAmount),
    );

    // In Memory Of
    formDataToSend.append("honoureeName", formData.honoureeName || "");
    formDataToSend.append("relationship", formData.relationship || "");

    // Totals & personal
    formDataToSend.append("totalDonationAmount", totalDonationAmount);
    formDataToSend.append("donorName", formData.donorName);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("mobile", formData.mobile);
    formDataToSend.append("dob", formData.dob);

    // Certificates / address
    formDataToSend.append("receive80G", formData.receive80G ? "Yes" : "No");
    formDataToSend.append(
      "panNumber",
      formData.receive80G ? formData.panNumber : "",
    );
    formDataToSend.append(
      "receiveMahaPrasadam",
      formData.receiveMahaPrasadam ? "Yes" : "No",
    );

    const addr = formData.mahaPrasadamDetails || {};
    formDataToSend.append("doorNumber", addr.doorNumber || "");
    formDataToSend.append("houseName", addr.houseName || "");
    formDataToSend.append("streetName", addr.streetName || "");
    formDataToSend.append("area", addr.area || "");
    formDataToSend.append("country", addr.country || "");
    formDataToSend.append("state", addr.state || "");
    formDataToSend.append("city", addr.city || "");
    formDataToSend.append("pincode", addr.pincode || "");

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbwimAkDj9mUjL4QnMEfCvVZPWv_BvkwmUMtbM-27RwuluXr8hi6BGv0_cJ51keJTvg0/exec",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formDataToSend.toString(),
        },
      );

      if (!response.ok) throw new Error("Network response was not ok");
      console.log("✅ Data stored successfully in Google Sheets");
    } catch (error) {
      console.warn("⚠️ Failed to store data in Google Sheets:", error);
    }

    return true;
  };

  // ---------------- JSX ----------------
  return (
    <div className="w-full flex justify-center items-center py-6 sm:py-10 px-3 sm:px-6">
      <div className="w-full max-w-[800px] bg-[#C61D21] py-8 sm:py-10 px-4 sm:px-8 rounded-3xl shadow-2xl">
        <form onSubmit={handleSubmit} className="text-white">
          <h2 className="font-dm-serif text-center text-2xl sm:text-3xl lg:text-4xl font-bold tracking-wide mb-6">
            Donation Details
          </h2>

          {/* ---------------- Sadhu Bhojana Seva ---------------- */}
          <div className="mt-4">
            <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[#F9E7D0] cursor-pointer font-semibold text-gray-800 hover:bg-[#f6d9b8] transition">
              <input
                type="checkbox"
                name="sadhuBhojanaSeva"
                checked={formData.sadhuBhojanaSeva}
                onChange={handleInputChange}
                className="w-5 h-5 text-red border-gray-300 rounded focus:ring-red"
              />
              <span className="text-base sm:text-lg">Sadhu Bhojana Seva</span>
            </label>

            {formData.sadhuBhojanaSeva && (
              <div className="grid grid-cols-2 gap-2.5 mt-3 sm:ml-4">
                {AMOUNTS.map((amount) => (
                  <button
                    type="button"
                    key={`sadhu-${amount}`}
                    onClick={() => handleAmountSelect("sadhu", amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl transition ${
                      formData.selectedSadhuAmount === amount
                        ? "bg-[#fac785] text-gray-900 ring-2 ring-orange"
                        : "bg-[#F9E7D0] text-gray-700 hover:bg-[#f6d9b8]"
                    }`}
                  >
                    ₹ {formatINR(amount)}
                  </button>
                ))}

                <input
                  type="number"
                  name="customSadhuAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customSadhuAmount}
                  onChange={(e) =>
                    handleCustomChange(
                      "selectedSadhuAmount",
                      "customSadhuAmount",
                      e.target.value,
                    )
                  }
                  className="col-span-2 p-3 border border-amber-200 rounded-xl my-1 text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>

          {/* ---------------- Gau Seva ---------------- */}
          <div className="mt-4">
            <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[#F9E7D0] cursor-pointer font-semibold text-gray-800 hover:bg-[#f6d9b8] transition">
              <input
                type="checkbox"
                name="gauSeva"
                checked={formData.gauSeva}
                onChange={handleInputChange}
                className="w-5 h-5 text-red border-gray-300 rounded focus:ring-red"
              />
              <span className="text-base sm:text-lg">Gau Seva</span>
            </label>

            {formData.gauSeva && (
              <div className="grid grid-cols-2 gap-2.5 mt-3 sm:ml-4">
                {AMOUNTS.map((amount) => (
                  <button
                    type="button"
                    key={`gau-${amount}`}
                    onClick={() => handleAmountSelect("gau", amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl transition ${
                      formData.selectedGauAmount === amount
                        ? "bg-[#fac785] text-gray-900 ring-2 ring-orange"
                        : "bg-[#F9E7D0] text-gray-700 hover:bg-[#f6d9b8]"
                    }`}
                  >
                    ₹ {formatINR(amount)}
                  </button>
                ))}

                <input
                  type="number"
                  name="customGauAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customGauAmount}
                  onChange={(e) =>
                    handleCustomChange(
                      "selectedGauAmount",
                      "customGauAmount",
                      e.target.value,
                    )
                  }
                  className="col-span-2 p-3 border border-amber-200 rounded-xl my-1 text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>

          {/* ---------------- Annadan Seva ---------------- */}
          <div className="mt-4">
            <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[#F9E7D0] cursor-pointer font-semibold text-gray-800 hover:bg-[#f6d9b8] transition">
              <input
                type="checkbox"
                name="annadanSeva"
                checked={formData.annadanSeva}
                onChange={handleInputChange}
                className="w-5 h-5 text-red border-gray-300 rounded focus:ring-red"
              />
              <span className="text-base sm:text-lg">Annadan Seva</span>
            </label>

            {formData.annadanSeva && (
              <div className="grid grid-cols-2 gap-2.5 mt-3 sm:ml-4">
                {AMOUNTS.map((amount) => (
                  <button
                    type="button"
                    key={`annadan-${amount}`}
                    onClick={() => handleAmountSelect("annadan", amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl transition ${
                      formData.selectedAnnadanAmount === amount
                        ? "bg-[#fac785] text-gray-900 ring-2 ring-orange"
                        : "bg-[#F9E7D0] text-gray-700 hover:bg-[#f6d9b8]"
                    }`}
                  >
                    ₹ {formatINR(amount)}
                  </button>
                ))}

                <input
                  type="number"
                  name="customAnnadanAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customAnnadanAmount}
                  onChange={(e) =>
                    handleCustomChange(
                      "selectedAnnadanAmount",
                      "customAnnadanAmount",
                      e.target.value,
                    )
                  }
                  className="col-span-2 p-3 border border-amber-200 rounded-xl my-1 text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>

          {/* ---------------- Archana Seva ---------------- */}
          <div className="mt-4">
            <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[#F9E7D0] cursor-pointer font-semibold text-gray-800 hover:bg-[#f6d9b8] transition">
              <input
                type="checkbox"
                name="archanaSeva"
                checked={formData.archanaSeva}
                onChange={handleInputChange}
                className="w-5 h-5 text-red border-gray-300 rounded focus:ring-red"
              />
              <span className="text-base sm:text-lg">Archana Seva</span>
            </label>

            {formData.archanaSeva && (
              <div className="grid grid-cols-2 gap-2.5 mt-3 sm:ml-4">
                {AMOUNTS.map((amount) => (
                  <button
                    type="button"
                    key={`archana-${amount}`}
                    onClick={() => handleAmountSelect("archana", amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl transition ${
                      formData.selectedArchanaAmount === amount
                        ? "bg-[#fac785] text-gray-900 ring-2 ring-orange"
                        : "bg-[#F9E7D0] text-gray-700 hover:bg-[#f6d9b8]"
                    }`}
                  >
                    ₹ {formatINR(amount)}
                  </button>
                ))}

                <input
                  type="number"
                  name="customArchanaAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customArchanaAmount}
                  onChange={(e) =>
                    handleCustomChange(
                      "selectedArchanaAmount",
                      "customArchanaAmount",
                      e.target.value,
                    )
                  }
                  className="col-span-2 p-3 border border-amber-200 rounded-xl my-1 text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>

          {/* ---------------- Sponsor Bhagavad Gita ---------------- */}
          <div className="mt-4">
            <label className="flex items-center gap-3 p-3.5 rounded-xl bg-[#F9E7D0] cursor-pointer font-semibold text-gray-800 hover:bg-[#f6d9b8] transition">
              <input
                type="checkbox"
                name="sponsorGita"
                checked={formData.sponsorGita}
                onChange={handleInputChange}
                className="w-5 h-5 text-red border-gray-300 rounded focus:ring-red"
              />
              <span className="text-base sm:text-lg">
                Sponsor Bhagavad Gita
              </span>
            </label>

            {formData.sponsorGita && (
              <div className="grid grid-cols-2 gap-2.5 mt-3 sm:ml-4">
                {BHAGVATGITAAMOUNTS.map(({ amount, text }) => (
                  <button
                    type="button"
                    key={`gita-${amount}`}
                    onClick={() => handleAmountSelect("gita", amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl transition ${
                      formData.selectedGitaAmount === amount
                        ? "bg-[#fac785] text-gray-900 ring-2 ring-orange"
                        : "bg-[#F9E7D0] text-gray-700 hover:bg-[#f6d9b8]"
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <span>₹ {formatINR(amount)}</span>
                      <span className="text-xs sm:text-sm font-medium">
                        {text}
                      </span>
                    </div>
                  </button>
                ))}

                <input
                  type="number"
                  name="customGitaAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customGitaAmount}
                  onChange={(e) =>
                    handleCustomChange(
                      "selectedGitaAmount",
                      "customGitaAmount",
                      e.target.value,
                    )
                  }
                  className="col-span-2 p-3 border border-amber-200 rounded-xl my-1 text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>

          {/* In Memory Of */}
          <div className="mt-6 pt-4 border-t border-red-700/50">
            <h3 className="text-center text-lg sm:text-xl font-semibold mb-3">
              In Memory Of
            </h3>
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text"
                name="honoureeName"
                value={formData.honoureeName}
                onChange={handleInputChange}
                placeholder="Honouree Name"
                className="w-full p-3 rounded-lg text-black outline-none focus:ring-2 focus:ring-amber-400"
              />
              <input
                type="text"
                name="relationship"
                value={formData.relationship}
                onChange={handleInputChange}
                placeholder="Relationship"
                className="w-full p-3 rounded-lg text-black outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          {/* Personal Details */}
          <div className="mt-6 pt-4 border-t border-red-700/50">
            <h3 className="text-center text-lg sm:text-xl font-semibold mb-3">
              Personal Details
            </h3>

            <label className="block mt-2">
              <input
                type="text"
                name="donorName"
                value={formData.donorName}
                onChange={handleInputChange}
                placeholder="Donor Name *"
                className="w-full p-3 rounded-lg text-black outline-none focus:ring-2 focus:ring-amber-400"
              />
              {errors.donorName && (
                <span className="block mt-1 ml-2 text-sm text-yellow-200">
                  {errors.donorName}
                </span>
              )}
            </label>

            <label className="block mt-3">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email ID *"
                className="w-full p-3 rounded-lg text-black outline-none focus:ring-2 focus:ring-amber-400"
              />
              {errors.email && (
                <span className="block mt-1 ml-2 text-sm text-yellow-200">
                  {errors.email}
                </span>
              )}
            </label>

            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              <label className="flex-1">
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Mobile Number *"
                  className="w-full px-3 py-2.5 rounded text-black outline-none focus:ring-2 focus:ring-amber-400"
                />
                {errors.mobile && (
                  <span className="block mt-1 ml-2 text-sm text-yellow-200">
                    {errors.mobile}
                  </span>
                )}
              </label>

              <label className="flex-1">
                <input
                  type="text"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  className="w-full p-3 rounded-lg text-black outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Date Of Birth *"
                  onFocus={(e) => (e.target.type = "date")}
                  onBlur={(e) => {
                    if (!e.target.value) e.target.type = "text";
                  }}
                />
                {errors.dob && (
                  <span className="block mt-1 ml-2 text-sm text-yellow-200">
                    {errors.dob}
                  </span>
                )}
              </label>
            </div>
          </div>

          <p className="text-amber-100/90 mt-2 text-sm text-center mb-0">
            (Sankalpa and Archana will be performed for you on your birthday)
          </p>

          <p className="text-white/80  text-sm text-center">
            By continuing, you are agreeing to{" "}
            <a
              href="/terms-and-conditions"
              className="underline text-amber-200 hover:text-white"
            >
              Terms of Use and Privacy Policy
            </a>
          </p>

          {/* Certificates & Logistics */}
          <div className="mt-4 pt-4 border-t border-red-700/50 space-y-4">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="receive80G"
                  checked={formData.receive80G}
                  onChange={handle80GCheckboxChange}
                  className="w-5 h-5 accent-blue-600 rounded"
                />
                <span className="text-sm sm:text-base text-white font-medium">
                  I would like to receive 80(G) Certificate
                </span>
              </label>

              {show80GDetails && (
                <div className="mt-3 bg-red-800/40 p-4 rounded-xl border border-red-500/30 space-y-2.5">
                  <p className="text-amber-100 text-xs">
                    Finance Act 2021 mandates uploading donation details for
                    80(G) deductions. Ensure PAN and address details are
                    provided.
                  </p>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    placeholder="PAN Number *"
                    className="w-full bg-white text-black px-4 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
                    required
                  />

                  {/* Address inputs for 80G */}
                  {[
                    "doorNumber",
                    "houseName",
                    "streetName",
                    "area",
                    "country",
                    "state",
                    "city",
                    "pincode",
                  ].map((key) => (
                    <input
                      key={`80g-${key}`}
                      type="text"
                      name={key}
                      placeholder={
                        {
                          doorNumber: "Door Number",
                          houseName: "House / Apartment / Building Name",
                          streetName: "Street Name",
                          area: "Location / Area",
                          country: "Country",
                          state: "State",
                          city: "City",
                          pincode: "Pincode",
                        }[key]
                      }
                      className="w-full bg-white text-black px-4 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
                      value={formData.mahaPrasadamDetails[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mahaPrasadamDetails: {
                            ...prev.mahaPrasadamDetails,
                            [key]: e.target.value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="receiveMahaPrasadam"
                  checked={formData.receiveMahaPrasadam}
                  onChange={handleMahaPrasadamCheckboxChange}
                  className="w-5 h-5 accent-blue-600 rounded"
                />
                <span className="text-sm sm:text-base text-white font-medium">
                  I would like to receive Maha prasadam (Only within India)
                </span>
              </label>

              {showMahaPrasadamDetails && (
                <div className="mt-3 bg-red-800/40 p-4 rounded-xl border border-red-500/30 space-y-2.5">
                  {[
                    "doorNumber",
                    "houseName",
                    "streetName",
                    "area",
                    "country",
                    "state",
                    "city",
                    "pincode",
                  ].map((key) => (
                    <input
                      key={`prasadam-${key}`}
                      type="text"
                      name={key}
                      placeholder={
                        {
                          doorNumber: "Door Number",
                          houseName: "House / Apartment / Building Name",
                          streetName: "Street Name",
                          area: "Location / Area",
                          country: "Country",
                          state: "State",
                          city: "City",
                          pincode: "Pincode",
                        }[key]
                      }
                      className="w-full bg-white text-black px-4 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-amber-400"
                      value={formData.mahaPrasadamDetails[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mahaPrasadamDetails: {
                            ...prev.mahaPrasadamDetails,
                            [key]: e.target.value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {errors.total && (
            <p className="text-yellow-300 text-sm mt-4 text-center font-semibold bg-red-900/50 p-2.5 rounded-lg">
              {errors.total}
            </p>
          )}

          <div className="flex justify-center mt-8">
            <button
              type="submit"
              className="bg-[#FAE29C] hover:bg-[#f6d77e] active:scale-95 transition-all text-gray-950 px-10 sm:px-14 py-3.5 rounded-2xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl cursor-pointer"
            >
              Donate{" "}
              {getTotalAmount() > 0 ? `₹ ${formatINR(getTotalAmount())}` : ""}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PitruPakshaDonationForm;
